// src/lib/accueil-releve.js — CE QUE L'ÉCRAN AFFICHE, DIT À L'OPÉRATEUR.
//
// ══ LA RÈGLE, ET D'OÙ ELLE VIENT ═══════════════════════════════════════════
//
// Dimitri, 2026-09-12 : « L'opérateur et l'accueil sont sur le même écran. Il
// devrait voir exactement ce que je vois, pas sa propre version. Si je vois
// "Chloé Raynal, Cocon Naturel, le 10 septembre", il doit voir la même ligne,
// pas la sienne. Ton correctif règle une liste — je veux la règle générale. »
//
// La règle générale, donc, et elle tient en une phrase :
//
//   ⚠️ CE QUE L'OPÉRATEUR SAIT DE L'ACCUEIL, C'EST L'ACCUEIL QUI LE LUI DONNE.
//      Il n'en refait AUCUNE version — ni la liste, ni l'ordre, ni les noms.
//
// ══ POURQUOI PAR LE NAVIGATEUR, ET NON PAR UNE SECONDE LECTURE ═════════════
//
// On aurait pu refaire les mêmes requêtes côté serveur et rejouer les mêmes
// calculs. C'est exactement ce qu'on vient de payer : deux chemins qui lisent
// la même table, un tri dans un sens ici, dans l'autre là, et sous un plafond
// commun de 6 les deux listes finissent DISJOINTES. Personne ne ment, et
// pourtant l'écran et l'opérateur ne parlent pas des mêmes voyageurs.
//
// Une seconde lecture ne peut jamais être identique à la première : elle ne
// connaît pas ce que Dimitri vient de taire d'un clic, ni ce qui a disparu de
// l'écran depuis le dernier chargement. L'état affiché n'existe QUE dans son
// navigateur — c'est donc son navigateur qui le dit.
//
// ══ CE QUI ENTRE, CE QUI RESTE DEHORS ══════════════════════════════════════
//
// Entrent : le titre et le sous-titre TELS QU'AFFICHÉS, l'ancienneté, la
// nature, l'ordre. Rien d'autre — pas d'identifiant technique, pas de clé, pas
// de destination. Le relevé sert à PARLER de ce qui est à l'écran ; agir passe
// par les leviers, et par le clic de Dimitri.
//
// ⚠️ LES NOMS DE VOYAGEURS Y SONT (décision Dimitri 2026-09-09 : « ma réserve
// sur les données personnelles tient pour les propriétaires, pas pour mes
// voyageurs — eux, je dois pouvoir les nommer »). Les adresses et téléphones,
// eux, sont masqués côté serveur par le scrubber commun : c'est là qu'est la
// barrière, parce qu'un texte venu du navigateur n'est jamais une preuve.
//
// ⚠️ ET « PAS ENCORE CHARGÉ » N'EST PAS « RIEN ». Un accueil dont les sources
// n'ont pas répondu enverrait trois listes vides, et l'opérateur annoncerait le
// calme sur un écran qui n'a rien lu. Le relevé porte donc l'état de chaque
// source, et le serveur en fait une phrase plutôt qu'un silence.

export const MAX_ATTEND = 25;
export const MAX_SURVEILLER = 20;
export const MAX_MESSAGES = 15;
// Un titre d'écran tient en une ligne ; au-delà, c'est qu'on a recopié autre
// chose. On coupe, et la coupure se voit.
export const MAX_CARS = 180;

function _t(v) {
  const s = typeof v === "string" ? v.trim() : "";
  if (!s) return "";
  return s.length > MAX_CARS ? `${s.slice(0, MAX_CARS)}…` : s;
}

function _jours(v) {
  return Number.isFinite(v) ? v : null;
}

/**
 * Le relevé de l'accueil, prêt à voyager dans `contexte.accueil`.
 *
 * ⚠️ ON REÇOIT LES LIGNES *RENDUES*, pas leurs sources. `attend` et
 * `surveiller` sont ce que l'écran vient de peindre — donc déjà partagées,
 * déjà triées par urgence, déjà privées de ce que Dimitri a tu. Leur
 * recalculer quoi que ce soit ici rouvrirait la porte qu'on ferme.
 *
 * @param {object} p
 * @param {string} p.jour — le jour de l'écran (ISO), celui de ses calculs
 * @param {Array} p.attend — « Ce qui vous attend », dans l'ordre affiché
 * @param {Array} p.surveiller — « À surveiller », les GROUPES tels qu'affichés
 * @param {Array} p.messages — « Messages en attente », dans l'ordre affiché
 * @param {object} p.sources — {nom: true|false|null} — null = pas encore lue
 * @returns {object} un objet BORNÉ, sans clé technique ni destination
 */
export function composerReleveAccueil({
  jour,
  // ⚠️ LE BANDEAU AUSSI (2026-09-12). C'est la PREMIÈRE chose qu'il voit, et
  // c'était une seconde lecture de plus : le serveur recomptait de son côté les
  // arrivées, les départs et les ménages du jour. Trois compteurs peints à
  // gauche, trois compteurs calculés à droite — la même mécanique de divergence
  // que les messages, sur les chiffres qu'il regarde en premier.
  bandeau,
  attend,
  surveiller,
  messages,
  // ⚠️ LE COMPTE VRAI, quand l'écran n'affiche qu'un extrait. La carte des
  // messages n'en rend que six et annonce le total à côté : sans ce
  // paramètre, l'opérateur conclurait sur six alors que Dimitri en voit le
  // nombre. On lui donne la même chose que la carte : ce qui est listé, et
  // combien il y en a.
  totaux,
  sources,
} = {}) {
  const att = (attend || [])
    .filter(Boolean)
    .slice(0, MAX_ATTEND)
    .map((l) => ({
      n: _t(l.nature),
      t: _t(l.titre),
      s: _t(l.sousTitre),
      j: _jours(l.jours),
    }))
    .filter((l) => l.t);
  const sur = (surveiller || [])
    .filter(Boolean)
    .slice(0, MAX_SURVEILLER)
    .map((g) => ({
      n: _t(g.nature),
      t: _t(g.titre),
      // ⚠️ Le sous-titre d'un groupe d'UNE ligne porte le nom du logement —
      // c'est tout ce qui la rend utile. Un groupe de plusieurs n'en a pas
      // (il serait celui d'un seul, donc faux pour les autres).
      s: _t(g.sousTitre),
      nb: Number.isFinite(g.n) ? g.n : 1,
    }))
    .filter((g) => g.t);
  const msg = (messages || [])
    .filter(Boolean)
    .slice(0, MAX_MESSAGES)
    .map((m) => ({
      // Le NOM tel qu'il est à l'écran. C'est toute la demande de Dimitri :
      // « Chloé Raynal », pas « un fil sur lc2 ».
      t: _t(m.titre),
      s: _t(m.sousTitre),
    }))
    .filter((m) => m.t);
  const b = bandeau && typeof bandeau === "object" ? bandeau : null;
  return {
    // ⚠️ LA MARQUE — et elle est fail-closed côté serveur. Sans elle, un objet
    // vide ou mal formé (un autre écran qui déclarerait `accueil: {}`) se
    // lirait « les trois listes sont vides », ferait taire la lecture de la
    // veille, et l'opérateur annoncerait le calme sur un écran qu'il n'a
    // jamais vu. Le serveur refuse tout ce qui ne la porte pas.
    de: "accueil",
    jour: _t(jour),
    bandeau: b
      ? {
          menages: (b.menages || [])
            .filter(Boolean)
            .slice(0, MAX_ATTEND)
            .map((m) => ({
              t: _t(m.titre),
              s: _t(m.sousTitre),
              seul: !!m.seul,
            })),
          arrivees: (b.arrivees || [])
            .filter(Boolean)
            .slice(0, MAX_ATTEND)
            .map((x) => _t(x.titre)),
          departs: (b.departs || [])
            .filter(Boolean)
            .slice(0, MAX_ATTEND)
            .map((x) => _t(x.titre)),
          aDecider: Number.isFinite(b.aDecider) ? b.aDecider : null,
        }
      : null,
    attend: att,
    surveiller: sur,
    messages: msg,
    // ⚠️ LES BORNES SE DISENT. Si l'écran affiche trente lignes et qu'on en
    // envoie vingt-cinq, l'opérateur doit pouvoir dire « il y en a d'autres »
    // plutôt que de conclure sur une liste qu'il croit entière.
    total: {
      attend:
        (totaux && totaux.attend) ?? (attend || []).filter(Boolean).length,
      // ⚠️ Le total des LIGNES, pas des groupes : c'est le compte que l'écran
      // affiche à côté de « À surveiller », et donc celui que Dimitri lit.
      surveiller:
        (totaux && totaux.surveiller) ??
        (surveiller || [])
          .filter(Boolean)
          .reduce((n, g) => n + (Number.isFinite(g.n) ? g.n : 1), 0),
      messages:
        (totaux && totaux.messages) ?? (messages || []).filter(Boolean).length,
    },
    sources: sources && typeof sources === "object" ? { ...sources } : null,
  };
}
